/**
 * LCFPIDFIMatching.js
 * DFI Matching Workspace modal for Finance-Ready Pipeline.
 * Shows top DFI matches, score breakdowns, and "Generate EoI" action.
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import {
  X, Loader, AlertCircle, ChevronRight, Target, Send,
} from "lucide-react";
import { toast } from "sonner";

const fmtU = v => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e9) return `$${(n/1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`;
  return `$${n}`;
};

function DFIMatchCard({ dfi, projectId, expanded, onToggle, onEoIGenerated }) {
  const [generatingEoI, setGeneratingEoI] = useState(false);
  const score = Number(dfi.suitability_score || 0);
  const scoreColor = score >= 70 ? "#22c55e" : score >= 50 ? "#f59e0b" : score >= 30 ? "#f97316" : "#ef4444";
  const factors = dfi.factor_breakdown || {};

  const generateEoI = async (e) => {
    e.stopPropagation();
    setGeneratingEoI(true);
    try {
      const r = await client.post("/lcfpi/eoi/track", {
        project_id: projectId, dfi_id: dfi.id, dfi_name: dfi.name,
      });
      toast.success(`EoI ${r.data?.eoi_id || ""} created for ${dfi.name}`);
      onEoIGenerated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "EoI generation failed");
    } finally {
      setGeneratingEoI(false);
    }
  };

  return (
    <div data-testid={`dfi-match-card-${dfi.id}`}
      className="border rounded-2xl overflow-hidden transition-all"
      style={{ borderColor: expanded ? `${scoreColor}50` : "#1e293b", background: "#0d1726" }}>

      <button onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-800/40 transition-all">
        <div className="w-12 h-12 rounded-xl flex-shrink-0 flex flex-col items-center justify-center"
          style={{ background: `${scoreColor}15`, border: `1.5px solid ${scoreColor}40` }}>
          <p className="text-base font-black" style={{ color: scoreColor }}>{score.toFixed(0)}</p>
          <p className="text-[8px] text-slate-500 font-semibold">/ 100</p>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-black text-white truncate">{dfi.name}</p>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ background: `${scoreColor}20`, color: scoreColor, border: `1px solid ${scoreColor}30` }}>
              {dfi.suitability_label || "—"}
            </span>
          </div>
          <p className="text-xs text-slate-400">{dfi.type} · {dfi.region}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            {fmtU(dfi.min_size_usd)} – {fmtU(dfi.max_size_usd)} ·{" "}
            {dfi.indicative_terms?.instrument || dfi.instruments?.[0] || "—"}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={generateEoI} disabled={generatingEoI || score < 30}
            data-testid={`dfi-generate-eoi-${dfi.id}`}
            className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-lg disabled:opacity-40"
            style={{ background: "#1d4ed820", border: "1px solid #1e40af40", color: "#93c5fd" }}>
            {generatingEoI ? <Loader size={10} className="animate-spin" /> : <Send size={10} />}
            EoI
          </button>
          <ChevronRight size={13} className="text-slate-600 transition-transform"
            style={{ transform: expanded ? "rotate(90deg)" : "none" }} />
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-800/50 pt-4 space-y-4">
          {/* Score breakdown */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Score Breakdown</p>
            <div className="space-y-2">
              {[
                ["Sector Alignment",    factors.sector_alignment,     "#22c55e", 20],
                ["Funding Size Fit",     factors.funding_size,         "#3b82f6", 20],
                ["Readiness Tier",       factors.readiness_tier,       "#a78bfa", 20],
                ["SDG Alignment",        factors.sdg_alignment,        "#0891b2", 15],
                ["Climate Performance",  factors.climate_performance,  "#34d399", 10],
                ["Governance Maturity",  factors.governance_maturity,  "#f59e0b", 10],
                ["Adaptation Relevance", factors.adaptation_relevance, "#22c55e",  5],
              ].map(([label, factor, color, maxPts]) => (
                <div key={label} className="grid items-center gap-3" style={{ gridTemplateColumns: "140px 1fr" }}>
                  <p className="text-[10px] text-slate-400">{label}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-800">
                      <div className="h-1.5 rounded-full"
                        style={{ width: `${Math.min(((factor?.score || 0) / maxPts) * 100, 100)}%`, background: color }} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-300" style={{ width: 36, textAlign: "right" }}>
                      {(factor?.score || 0)}/{maxPts}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key info grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/40 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-1">Instrument Type</p>
              <p className="text-xs font-bold text-white">
                {dfi.indicative_terms?.instrument || dfi.instruments?.[0] || "—"}
              </p>
            </div>
            <div className="bg-slate-800/40 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-1">Ticket Range</p>
              <p className="text-xs font-bold text-white">{fmtU(dfi.min_size_usd)} – {fmtU(dfi.max_size_usd)}</p>
            </div>
            <div className="bg-slate-800/40 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-1">Sector Fit</p>
              <p className="text-xs font-bold text-white capitalize">
                {factors.sector_alignment?.match ? "Direct match" : "Adjacent"}
              </p>
            </div>
            <div className="bg-slate-800/40 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 mb-1">Tenor</p>
              <p className="text-xs font-bold text-white">
                {dfi.indicative_terms?.tenor || dfi.tenor_years || "—"} yrs
              </p>
            </div>
          </div>

          {/* Missing requirements */}
          {score < 70 && (
            <div className="bg-amber-900/10 border border-amber-700/20 rounded-xl p-3">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-2">
                Missing Requirements
              </p>
              <div className="space-y-1">
                {[
                  factors.funding_size?.in_range === false && `Funding: ${factors.funding_size?.detail}`,
                  (factors.readiness_tier?.score || 0) < 15 && "Readiness tier improvement needed",
                  (factors.climate_performance?.score || 0) < 7 && "Climate score below DFI threshold",
                  (factors.governance_maturity?.score || 0) < 7 && "Governance documentation gaps",
                ].filter(Boolean).slice(0, 3).map((gap, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <AlertCircle size={10} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-200/80">{gap}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next action */}
          <div className="bg-blue-900/15 border border-blue-700/25 rounded-xl p-3">
            <p className="text-[10px] font-bold text-blue-300 uppercase tracking-wider mb-1">Next Action</p>
            <p className="text-xs text-blue-200/90 leading-relaxed">{dfi.next_action}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DFI Matching Modal ────────────────────────────────────────────────────────
export function DFIMatchingModal({ project, onClose }) {
  const [matches,  setMatches]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!project?.project_id) return;
    client.get(`/lcfpi/dfi-matching/${project.project_id}?top_n=5`)
      .then(r => setMatches(r.data?.matches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project?.project_id]);

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end"
      style={{ background: "#000000bb" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="h-full flex flex-col" data-testid="dfi-matching-modal"
        style={{ width: "min(700px,100vw)", background: "#060d1a", borderLeft: "1px solid #1e293b" }}>

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <Target size={14} color="#a78bfa" />
              <p className="text-sm font-black text-white">DFI Matching Workspace</p>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5">
              {project?.org_name} · {project?.readiness_tier} · {project?.sector?.replace(/_/g, " ")}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 px-6 pt-4 pb-3 flex-shrink-0">
          {[
            ["Funding Need",    fmtU(project?.funding_need_usd),                           "#60a5fa"],
            ["Readiness Score", `${Number(project?.readiness_score||0).toFixed(0)}/100`,   "#22c55e"],
            ["Climate Score",   `${Number(project?.climate_score||0).toFixed(0)}/100`,     "#f59e0b"],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
              <p className="text-base font-black" style={{ color: c }}>{v}</p>
              <p className="text-xs text-slate-400 mt-0.5">{l}</p>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading && (
            <div className="flex justify-center py-12">
              <Loader size={18} className="animate-spin text-slate-500" />
            </div>
          )}
          {!loading && matches.length === 0 && (
            <div className="text-center py-12 text-slate-600">
              <Target size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No DFI matches</p>
              <p className="text-xs mt-1">Run project assessment first</p>
            </div>
          )}
          {!loading && matches.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                Top {matches.length} DFI Matches · Click to expand
              </p>
              {matches.map(dfi => (
                <DFIMatchCard key={dfi.id} dfi={dfi} projectId={project.project_id}
                  expanded={expanded === dfi.id}
                  onToggle={() => toggleExpand(dfi.id)}
                  onEoIGenerated={() => {}} />
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-800 flex-shrink-0">
          <p className="text-[9px] text-slate-700">
            DFI scores are indicative · Based on public mandate, sector, ticket size, and readiness tier
          </p>
        </div>
      </div>
    </div>
  );
}
